import { useCallback } from 'react';

/**
 * 🔊 Global Sound Hook
 * Provides audio feedback for user interactions using native Audio API
 * 
 * Available sounds:
 * - login: User login
 * - logout: User logout
 * - success: Successful operations (save, update, create)
 * - error: Error operations (validation, API errors)
 * - click: Button clicks
 * - notification: Toast notifications
 * - pageChange: Page transitions
 */

const useSounds = () => {
  // Play sound using native Audio API
  const playSound = useCallback((soundFile, volume = 0.3) => {
    try {
      const audio = new Audio(soundFile);
      audio.volume = volume;
      audio.play().catch(err => console.warn('Audio play failed:', err));
    } catch (error) {
      console.warn('Sound initialization failed:', error);
    }
  }, []);

  // Sound functions
  const login = useCallback(() => {
    playSound('/sounds/login.wav', 0.5);
  }, [playSound]);

  const logout = useCallback(() => {
    playSound('/sounds/logout.wav', 0.5);
  }, [playSound]);

  const success = useCallback(() => {
    playSound('/sounds/success.wav', 0.3);
  }, [playSound]);

  const error = useCallback(() => {
    playSound('/sounds/error.wav', 0.3);
  }, [playSound]);

  const click = useCallback(() => {
    playSound('/sounds/click.wav', 0.2);
  }, [playSound]);

  const notification = useCallback(() => {
    playSound('/sounds/notification.wav', 0.25);
  }, [playSound]);

  const pageChange = useCallback(() => {
    playSound('/sounds/page-change.wav', 0.15);
  }, [playSound]);

  return {
    login,
    logout,
    success,
    error,
    click,
    notification,
    pageChange
  };
};

export default useSounds;
